"use client";

import React from "react";
import {
  ALL_TIMEZONES,
  DEFAULT_TIMEZONE,
  POPULAR_TIMEZONES,
} from "@/lib/timezones";

const CTA_GREEN = "#0dfe00";

type Variant = "light" | "dark";

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function safeNextBorder(variant: Variant) {
  return variant === "dark" ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.18)"; // slate-900 @ low alpha
}

export default function TimezonePicker({
  name,
  defaultValue,
  description,
  variant = "light",
  label = "Workspace timezone",
}: {
  name: string;
  defaultValue?: string;
  description?: string;
  variant?: Variant;
  label?: string;
}) {
  const [query, setQuery] = React.useState("");
  const [customValue, setCustomValue] = React.useState<string | null>(null);

  const dv = defaultValue ?? DEFAULT_TIMEZONE;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return ALL_TIMEZONES.filter((t) =>
      t.value.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [query]);

  const isDark = variant === "dark";

  const labelClass = isDark
    ? "text-sm font-medium text-neutral-300"
    : "text-sm font-medium text-slate-700";

  const selectClass = cx(
    "mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2",
    isDark
      ? "border-white/10 bg-white/5 text-neutral-200"
      : "border-slate-300 bg-white text-slate-900",
    // ring color: subtle in light, CTA-ish glow is done inline for consistency
    isDark ? "focus:ring-white/20" : "focus:ring-slate-400/30"
  );

  const optionClass = isDark
    ? "bg-neutral-900 text-neutral-200"
    : "bg-white text-slate-900";

  const optionHeaderClass = isDark
    ? "bg-neutral-900 text-neutral-400"
    : "bg-white text-slate-500";

  const searchInputClass = cx(
    "w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2",
    isDark
      ? "border-white/10 bg-white/5 text-neutral-200 placeholder:text-neutral-500 focus:ring-white/20"
      : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:ring-slate-400/30"
  );

  const resultsPanelClass = cx(
    "rounded-xl border p-2",
    isDark
      ? "border-white/10 bg-neutral-900/80 backdrop-blur"
      : "border-slate-200 bg-white"
  );

  const resultsTitleClass = isDark
    ? "mb-1 text-xs font-medium text-neutral-400"
    : "mb-1 text-xs font-medium text-slate-600";

  const resultButtonClass = cx(
    "rounded-md px-2 py-1 text-left text-sm transition focus:outline-none focus:ring-2",
    isDark
      ? "text-neutral-200 focus:ring-white/20 hover:bg-[rgba(13,254,0,0.12)] active:bg-[rgba(13,254,0,0.18)]"
      : "text-slate-900 focus:ring-slate-400/30 hover:bg-[rgba(13,254,0,0.14)] active:bg-[rgba(13,254,0,0.22)]"
  );

  const descriptionClass = isDark ? "mt-2 text-xs text-neutral-500" : "mt-2 text-xs text-slate-500";

  return (
    <div>
      <label className={labelClass}>{label}</label>

      <select
        name={name}
        defaultValue={dv}
        className={selectClass}
        style={{ boxShadow: "none" }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0 2px rgba(13,254,0,0.25)`;
          e.currentTarget.style.borderColor = "rgba(13,254,0,0.35)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = safeNextBorder(variant);
        }}
        onChange={() => {
          setCustomValue(null);
          setQuery("");
        }}
      >
        {/* Header row (disabled option instead of <optgroup>) */}
        <option value="" disabled className={optionHeaderClass}>
          — Popular —
        </option>
        {POPULAR_TIMEZONES.map((tz) => (
          <option key={tz.value} value={tz.value} className={optionClass}>
            {tz.label}
          </option>
        ))}

        <option value="" disabled className={optionHeaderClass}>
          — All —
        </option>
        {ALL_TIMEZONES.map((tz) => (
          <option key={tz.value} value={tz.value} className={optionClass}>
            {tz.label}
          </option>
        ))}
      </select>

      <div className="mt-3 space-y-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search timezones (e.g., London, Tokyo, São Paulo)"
          className={searchInputClass}
        />

        {filtered.length > 0 ? (
          <div className={resultsPanelClass}>
            <div className={resultsTitleClass}>Search results</div>
            <div className="grid gap-1">
              {filtered.map((tz) => (
                <button
                  key={tz.value}
                  type="button"
                  className={resultButtonClass}
                  onClick={() => {
                    setCustomValue(tz.value);
                    setQuery("");
                  }}
                >
                  {tz.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {customValue ? <input type="hidden" name={name} value={customValue} /> : null}
      </div>

      {description ? <div className={descriptionClass}>{description}</div> : null}
    </div>
  );
}
